import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { InputFormField } from '@/components/formFields/InputFormField';
import { SelectFormField } from '@/components/formFields/SelectFormField';
import { AreaFormField } from '@/components/formFields/AreaFormField';
import { Manufacturer } from '../../../manufacturer/hooks/useManufacturer';
import { Consultant, useCreateConsultant, useUpdateConsultant } from '../../hooks/useConsultant';
import { getFieldErrors } from '@/utils/apiError';
import { DialogClose } from '@radix-ui/react-dialog';

type ConsultantModalProps = {
  visible: boolean;
  title: string;
  formProps?: Consultant | null;
  manufacturers: Manufacturer[];
  manufacturersLoading?: boolean;
  onClose: () => void;
  onSaved?: (id: string) => void;
};

const ValidationSchema = Yup.object().shape({
  first_name: Yup.string().nullable().required('Povinné pole'),
  last_name: Yup.string().nullable().required('Povinné pole'),
  manufacturer: Yup.string().nullable().required('Povinné pole'),
  email: Yup.string().nullable().email('Neplatný e-mail'),
  phone: Yup.string().nullable(),
  note: Yup.string().nullable(),
});

const ConsultantModal = ({
  visible,
  title,
  formProps,
  manufacturers,
  manufacturersLoading,
  onClose,
  onSaved,
}: ConsultantModalProps) => {
  const create = useCreateConsultant();
  const update = useUpdateConsultant();

  const form = useFormik({
    enableReinitialize: true,
    validationSchema: ValidationSchema,
    initialValues: {
      first_name: formProps?.first_name || '',
      last_name: formProps?.last_name || '',
      manufacturer: formProps?.manufacturer || '',
      email: formProps?.email || '',
      phone: formProps?.phone || '',
      note: formProps?.note || '',
    },
    onSubmit: (values, { setErrors }) => {
      const payload = {
        first_name: values.first_name,
        last_name: values.last_name,
        manufacturer: values.manufacturer,
        email: values.email || null,
        phone: values.phone || null,
        note: values.note || null,
      };
      const onSuccess = (res: { data: Consultant }) => {
        onSaved?.(res.data.id);
        onClose();
      };
      const onError = (error: unknown) => setErrors(getFieldErrors(error));

      if (formProps?.id) {
        update.mutate({ id: formProps.id, values: payload }, { onSuccess, onError });
      } else {
        create.mutate(payload, { onSuccess, onError });
      }
    },
  });

  const isSaving = create.isLoading || update.isLoading;

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <FormikProvider value={form}>
            <form onSubmit={form.handleSubmit} className="space-y-3">
              <InputFormField name="first_name" label="Jméno" placeholder="Jméno" required />
              <InputFormField name="last_name" label="Příjmení" placeholder="Příjmení" required />
              <SelectFormField
                name="manufacturer"
                label="Výrobce"
                placeholder="Vyberte výrobce"
                data={manufacturers}
                loading={manufacturersLoading}
                required
              />
              <InputFormField
                name="email"
                label="E-mail"
                placeholder="e-mail"
                type="email"
                required
              />
              <InputFormField name="phone" label="Telefon" placeholder="Telefon" required />
              <AreaFormField
                name="note"
                label="Poznámka"
                placeholder="Poznámka"
                rows={3}
                required
              />
            </form>
          </FormikProvider>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" data-testid="consultant-modal-close-btn">
              Zavřít
            </Button>
          </DialogClose>
          <Button
            onClick={() => form.submitForm()}
            disabled={isSaving}
            data-testid="consultant-modal-submit-btn"
          >
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConsultantModal;
